/**
 * MarginBridge — Sage against the invoice fact, month by month, at ANY scope.
 *
 * WHAT IT REPLACED, AND WHY. At entity scope the bottom of /margin showed the Group→Entity
 * composition — Kirkstall beside Oracle beside a Group total — while the whole page above it
 * reported one site. It decomposed a number the page was not claiming. The question at entity
 * scope is whether OUR figures agree with the ledger, and the two share a clock (the invoice
 * date), so the grain is the month.
 *
 * ⭐ AND GROUP ASKS THE SAME QUESTION (Mike, 24 Aug 2026). Group got the entity split ALONE,
 * which reports the whole period as one number while both entities got that period month by
 * month — so the parent carried less than its children. It is the same test at Group: both
 * sides sum the same two entities, so revenue stays like for like, and the pooled row reads
 * Sage £4,054,848 against £4,115,592, +1.5%. The split is still shown, below this table.
 *
 * ⚠ ONLY REVENUE IS GRADED, and the footer says why. Both sides count the same billed lines,
 * so a revenue miss is a real finding. Cost is a period posting in Sage at BOTH sites —
 * Kirkstall's own Sage gross margin swings 24.2 points across 2026 and still ties to
 * job-actual within 1.3% pooled — so a red chip on a monthly cost delta would be a red chip on
 * a correct number. The pooled TOTAL row is where the cost comparison earns its keep.
 *
 * ⚠ THE `COSTED` COLUMN IS LOAD BEARING. Our GM% is taken on the costed portion, not on billed
 * revenue. Without it the row reads £187,892.93 · £95,024.85 · 41.5% and a reader who subtracts
 * gets 49.4%. It is the same omission the coverage band fixed for the cards.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from './InfoBanner'
import { formatCurrency } from '../utils/formatters'
import { EM_DASH, periodHeading } from './kit/cellFormat'
import { bridge, type BridgeRow, type Grade } from '../viewmodels/marginBridge'
import type { useMarginTimeline } from '../hooks/useMargin'
import type { Cell, Site } from '../types/metrics'

const HEAD = 'px-3 py-2.5 border-b border-line font-mono text-[10px] uppercase tracking-[.06em] text-mut2'
const CELL = 'px-3 py-2 border-b border-line2 text-right tabular-nums'
const GRADE: Record<Grade, string> = {
  good: 'text-good',
  watch: 'text-amber',
  bad: 'text-bad',
  none: 'text-mut2',
}

const gbp = (v: number | null): string => (v === null ? EM_DASH : formatCurrency(v, '£', 0))
const pct = (v: number | null): string => (v === null ? EM_DASH : `${v.toFixed(1)}%`)
const signed = (v: number | null, unit: string): string =>
  v === null ? EM_DASH : `${v > 0 ? '+' : ''}${v.toFixed(1)}${unit}`

/** A group header spanning its columns, so the eye reads "Sage · ours · difference" once
 * rather than re-deriving it from nine column names. */
function Groups(): React.JSX.Element {
  const cap = 'px-3 pt-2.5 pb-1 font-mono text-[9.5px] uppercase tracking-[.09em]'
  return (
    <tr>
      <th className={`${cap} text-left text-mut2`} />
      <th className={`${cap} text-right text-brand`} colSpan={3}>Revenue — the test</th>
      <th className={`${cap} text-right text-mut2`} colSpan={4}>Cost</th>
      <th className={`${cap} text-right text-mut2`} colSpan={3}>Gross margin</th>
    </tr>
  )
}

/** The column names. Sage and ERP repeat by design — the group header above says which
 * measure each trio belongs to, so the name here only has to say whose figure it is.
 *
 * ⚠ IT SAID "CORTEX" UNTIL 24 Aug 2026, which named the TOOL and not the source. Cortex is
 * what draws the table; it is not where either number came from. The comparison is the finance
 * LEDGER against the operational system that raised the invoices — FactoryMaster at Kirkstall,
 * Red Ant at Oracle. "Entity" would be wrong for the same reason in reverse: both sides are
 * the same entity, because the card is already scoped to one. */
const COLUMNS = ['Invoice month', 'Sage', 'ERP', 'Δ', 'Sage COGS', 'ERP', 'Δ',
                 'Costed %', 'Sage', 'ERP', 'Δ'] as const

/** The system each entity's invoices actually come from, so the subtitle can name it rather
 * than making the reader know. One entity maps to one source system in the mart and has since
 * the fact was built.
 *
 * ⚠ GROUP READS BOTH, AND SAID SO NOWHERE until 24 Aug 2026 — it fell to the generic word, and
 * MarginCoverage prints that word after "the", so the Group cards read "the the ERP invoice".
 * Naming the pair fixes the sentence and the omission together. */
export const ERP_OF: Record<string, string> = {
  Kirkstall: 'FactoryMaster',
  Oracle: 'Red Ant',
  Group: 'FactoryMaster + Red Ant',
}

function Head(): React.JSX.Element {
  return (
    <tr>
      {COLUMNS.map((name, i) => (
        <th key={`${name}-${i}`} className={`${HEAD} ${i === 0 ? 'text-left' : 'text-right'}`}>{name}</th>
      ))}
    </tr>
  )
}

function Row({ r }: { r: BridgeRow }): React.JSX.Element {
  const tone = r.isTotal ? 'font-semibold text-ink' : ''
  const edge = r.isTotal ? 'border-t-2 border-line bg-panel2' : ''
  return (
    <tr className={`cx-row ${edge}`}>
      <td className={`px-3 py-2 border-b border-line2 font-medium text-ink ${tone}`}>{r.label}</td>
      <td className={`${CELL} ${tone}`}>{gbp(r.sageRevenue)}</td>
      <td className={`${CELL} ${tone}`}>{gbp(r.ourRevenue)}</td>
      <td className={`${CELL} font-semibold ${GRADE[r.revenueGrade]}`}>{signed(r.revenueDeltaPct, '%')}</td>
      <td className={`${CELL} ${tone}`}>{gbp(r.sageCogs)}</td>
      <td className={`${CELL} ${tone}`}>{gbp(r.ourCost)}</td>
      <td className={`${CELL} text-mut2`}>{signed(r.costDeltaPct, '%')}</td>
      <td className={`${CELL} text-mut2`}>{pct(r.coveragePct)}</td>
      <td className={`${CELL} ${tone}`}>{pct(r.sageGmPct)}</td>
      <td className={`${CELL} ${tone}`}>{pct(r.ourGmPct)}</td>
      <td className={`${CELL} text-mut2`}>{signed(r.gmDeltaPt, 'pt')}</td>
    </tr>
  )
}

/** What the grading means and what it deliberately does not cover. A reader who sees one
 * graded column and three plain ones will assume the plain ones passed, so this says otherwise
 * in the same eye-line as the table. */
function Footer({ site, missing }: {
  site: Site
  missing: { sageOnly: string[]; oursOnly: string[] }
}): React.JSX.Element {
  // A served id is an ADDRESS. The reader gets 'Feb 26', the same as every other month label
  // on the platform — a raw '2026-02' in a sentence is the id leaking into the copy.
  const sageOnly = missing.sageOnly.map(periodHeading)
  const oursOnly = missing.oursOnly.map(periodHeading)
  return (
    <div className="space-y-1.5 border-t border-line2 px-4 py-2.5 text-[11.5px] leading-relaxed text-mut2">
      <p>
        <span className="font-semibold text-ink">Revenue is the leg that must agree</span>, and it
        is the only one graded: both sides count the same billed lines in the same month, on the
        invoice date. Under 1% is a match, 1–5% is worth a look, over 5% wants an explanation.
      </p>
      <p>
        <span className="font-semibold text-ink">The monthly cost columns are not a variance.</span>{' '}
        Sage posts cost by period rather than against the jobs that earned the month&apos;s
        revenue — Kirkstall&apos;s own Sage gross margin swings 24.2 points across 2026 and still
        ties to job cost within 1.3% pooled. Read the total row, not the months.
      </p>
      <p>
        <span className="font-semibold text-ink">Costed</span> is the share of our revenue that
        carries a cost, and it is the denominator our GM% is taken on. Our cost column prices only
        that share, so it is not comparable to a COGS figure covering all of it.
      </p>
      {sageOnly.length > 0 && (
        <p className="text-amber">
          Sage posted {sageOnly.join(', ')} and we billed nothing into{' '}
          {sageOnly.length === 1 ? 'it' : 'them'} at {site}.
        </p>
      )}
      {oursOnly.length > 0 && (
        <p className="text-amber">
          We billed into {oursOnly.join(', ')} and Sage has posted no revenue there.
        </p>
      )}
    </div>
  )
}

export default function MarginBridge({ lookup, months, site, query, windowLabel }: {
  lookup: Map<string, Cell>
  /** The closed Sage months in the scoped window (viewmodels/margin windowSummary). */
  months: string[]
  site: Site
  /** The SAME timeline query the panel above reads, so the two can never cover different work. */
  query: ReturnType<typeof useMarginTimeline>
  windowLabel: string
}): React.JSX.Element {
  if (query.error) {
    return <InfoBanner variant="error" icon={AlertCircle} title="Reconciliation unavailable" message={query.error.message} />
  }
  const b = bridge(lookup, months, site, query.data ?? null, windowLabel)
  return (
    <>
      <div className="cx-scroll overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-xs">
          <thead>
            <Groups />
            <Head />
          </thead>
          <tbody>
            {b.rows.map((r) => (
              <Row key={r.period} r={r} />
            ))}
          </tbody>
        </table>
      </div>
      <Footer site={site} missing={b.missing} />
    </>
  )
}
